import type { Association } from "@entities";
import type { ActiveFilter } from "@shared/frontend/hyper-table/filter-types";

/** Matches server `RelevantVolunteersFiltersDTO` / feed-kysely filter options. */
export type RankedVolunteersTableFilters = {
  minAge?: number;
  maxAge?: number;
  genders?: string[];
  activityStatuses?: string[];
  statusIds?: string[];
  noStatus?: boolean;
  commentsContains?: string;
  teamIds?: string[];
  skillIds?: string[];
  hasVehicle?: boolean;
  hasNumberId?: boolean;
  phoneContains?: string;
  nameContains?: string;
  addressContains?: string;
  addressCoordinates?: { lat: number; lng: number };
  addressRadiusKm?: number;
  preferredCategoryIds?: string[];
  preferredPopulationIds?: string[];
  lastVolunteeringDate?: { start?: Date; end?: Date };
  createdAt?: { start?: Date; end?: Date };
  updatedAt?: { start?: Date; end?: Date };
};

function parseOptionalInt(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const items = value.filter((v): v is string => typeof v === "string");
  return items.length > 0 ? items : undefined;
}

function parseOptionalDate(value: unknown): Date | undefined {
  if (value == null) return undefined;
  const d = value instanceof Date ? value : new Date(value as string | number);
  return Number.isFinite(d.getTime()) ? d : undefined;
}

function dateRangeFromMeta(meta: Record<string, unknown> | undefined) {
  const start = parseOptionalDate(meta?.startDate);
  const end = parseOptionalDate(meta?.endDate);
  if (!start && !end) return undefined;
  return { start, end };
}

function filterMeta(
  activeFilters: ActiveFilter<Association>[],
  id: string,
): Record<string, unknown> | undefined {
  const filter = activeFilters.find((f) => f.id === id);
  if (!filter?.meta || typeof filter.meta !== "object") return undefined;
  return filter.meta as Record<string, unknown>;
}

/** Map HyperTable volunteer toolbar filters to ranked-users API filters. */
export function mapActiveFiltersToRankedFilters(
  activeFilters: ActiveFilter<Association>[],
): RankedVolunteersTableFilters | undefined {
  const result: RankedVolunteersTableFilters = {};

  const nameMeta = filterMeta(activeFilters, "name");
  const nameContains = parseOptionalString(nameMeta?.value);
  if (nameContains) result.nameContains = nameContains;

  const genderMeta = filterMeta(activeFilters, "gender");
  const genders = parseStringArray(genderMeta?.selectedOptions);
  if (genders) result.genders = genders;

  const addressMeta = filterMeta(activeFilters, "address");
  const addressContains = parseOptionalString(addressMeta?.text);
  if (addressContains) result.addressContains = addressContains;

  const numberIdMeta = filterMeta(activeFilters, "numberID");
  if (typeof numberIdMeta?.value === "boolean") {
    result.hasNumberId = numberIdMeta.value;
  }

  const teamsMeta = filterMeta(activeFilters, "teams");
  const teamIds = parseStringArray(teamsMeta?.teams);
  if (teamIds) result.teamIds = teamIds;

  const skillsMeta = filterMeta(activeFilters, "skills");
  const skillIds = parseStringArray(skillsMeta?.skills);
  if (skillIds) result.skillIds = skillIds;

  const hasVehicleMeta = filterMeta(activeFilters, "hasVehicle");
  if (hasVehicleMeta?.checked === true) result.hasVehicle = true;

  const activityMeta = filterMeta(activeFilters, "activityStatus");
  const activityStatus = parseOptionalString(activityMeta?.value);
  if (activityStatus) result.activityStatuses = [activityStatus];

  const statusMeta = filterMeta(activeFilters, "status");
  const statusIds = parseStringArray(statusMeta?.statuses);
  if (statusIds) result.statusIds = statusIds;
  if (statusMeta?.noStatus === true) result.noStatus = true;

  const phoneMeta = filterMeta(activeFilters, "phoneNumber");
  const phoneContains = parseOptionalString(phoneMeta?.value);
  if (phoneContains) result.phoneContains = phoneContains;

  const ageMeta = filterMeta(activeFilters, "age");
  const minAge = parseOptionalInt(ageMeta?.minAge);
  const maxAge = parseOptionalInt(ageMeta?.maxAge);
  if (minAge != null) result.minAge = minAge;
  if (maxAge != null) result.maxAge = maxAge;

  const locationMeta = filterMeta(activeFilters, "location");
  const lat = Number(locationMeta?.latitude);
  const lng = Number(locationMeta?.longitude);
  const addressRadiusKm = parseOptionalInt(locationMeta?.distanceKm);
  if (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat !== 0 &&
    lng !== 0 &&
    addressRadiusKm != null &&
    addressRadiusKm > 0
  ) {
    result.addressCoordinates = { lat, lng };
    result.addressRadiusKm = addressRadiusKm;
  }

  const categoriesMeta = filterMeta(activeFilters, "preferredCategories");
  const preferredCategoryIds = parseStringArray(
    categoriesMeta?.selectedCategoryIds,
  );
  if (preferredCategoryIds) result.preferredCategoryIds = preferredCategoryIds;

  const populationsMeta = filterMeta(activeFilters, "preferredPopulations");
  const preferredPopulationIds = parseStringArray(
    populationsMeta?.selectedPopulationIds,
  );
  if (preferredPopulationIds) {
    result.preferredPopulationIds = preferredPopulationIds;
  }

  const commentsMeta = filterMeta(activeFilters, "comments");
  const commentsContains = parseOptionalString(commentsMeta?.value);
  if (commentsContains) result.commentsContains = commentsContains;

  const lastVol = dateRangeFromMeta(filterMeta(activeFilters, "lastVolunteeringDate"));
  if (lastVol) result.lastVolunteeringDate = lastVol;

  const createdAt = dateRangeFromMeta(filterMeta(activeFilters, "createdAt"));
  if (createdAt) result.createdAt = createdAt;

  const updatedAt = dateRangeFromMeta(filterMeta(activeFilters, "updatedAt"));
  if (updatedAt) result.updatedAt = updatedAt;

  return Object.keys(result).length > 0 ? result : undefined;
}

export function rankedFiltersCacheKey(
  filters: RankedVolunteersTableFilters | undefined,
): string {
  if (!filters) return "";
  return JSON.stringify(filters, (_key, value) =>
    value instanceof Date ? value.toISOString() : value,
  );
}
